import {useEffect, useMemo, useRef} from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Save} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {RfcAttachmentType, RfcStatus, RfcStep} from '@common/types/rfc';
import {
  getBlankRfcFormState,
  rfcSection125FormDraftSchema,
  rfcMecFormDraftSchema,
  rfcClientPortalAccessFormDraftSchema,
} from '@common/lib/rfcSchema';
import {RfcStepForm} from '@client/pages/rfc/forms/RfcStepForm';
import {toast} from '@common-web/hooks/use-toast';
import {surfaceRfcFormErrors, applyRfcFieldErrors} from '@client/pages/rfc/rfcFormErrors';
import {RfcContext} from '@client/pages/rfc/RfcContext';
import {useAuthStore} from '@common-web/stores/authStore';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';

const wizardFormSchema = z.object({
  section125Form: rfcSection125FormDraftSchema,
  mecForm: rfcMecFormDraftSchema,
  clientPortalAccessForm: rfcClientPortalAccessFormDraftSchema,
});
type WizardFormData = z.infer<typeof wizardFormSchema>;

interface AdminSection {
  title: string;
  step: RfcStep;
}

function getAdminSections(hasSection125: boolean, hasMec: boolean, hasClientPortalAccess: boolean) {
  const sections: {heading: string; items: AdminSection[]}[] = [];
  if (hasSection125) {
    sections.push({
      heading: 'Section 125 Program',
      items: [
        {title: 'Group Information', step: RfcStep.SECTION_125_FORM_GROUP_INFO},
        {title: 'Eligibility & Admin', step: RfcStep.SECTION_125_FORM_ADMIN},
        {title: 'Contacts', step: RfcStep.SECTION_125_FORM_CONTACTS},
        {title: 'Payroll', step: RfcStep.SECTION_125_FORM_PAYROLL_INFO},
        {title: 'Agent of Record', step: RfcStep.SECTION_125_FORM_AGENT},
      ],
    });
  }
  if (hasMec) {
    sections.push({
      heading: 'MEC Plan',
      items: [
        {title: 'Group Information', step: RfcStep.MEC_FORM_GROUP_INFO},
        {title: 'Eligibility & Admin', step: RfcStep.MEC_FORM_ADMIN},
        {title: 'Contacts', step: RfcStep.MEC_FORM_CONTACTS},
        {title: 'Payroll', step: RfcStep.MEC_FORM_PAYROLL_INFO},
        {title: 'Agent of Record', step: RfcStep.MEC_FORM_AGENT},
      ],
    });
  }
  sections.push({
    heading: 'Additional',
    items: [
      {title: 'ACH Authorization', step: RfcStep.ACH_FORM},
      ...(hasClientPortalAccess
        ? [{title: 'Client Portal Access', step: RfcStep.CLIENT_PORTAL_ACCESS}]
        : []),
    ],
  });
  return sections;
}

export function AdminRfcEditor({
  rfcId,
  onCancel,
  onSaved,
}: {
  rfcId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const user = useAuthStore((state) => state.user);
  const canViewPepm = useHasPermission(CODE_PERMISSIONS.rfc.viewPepm.key);
  const isClientAdmin = !!user?.clientProfile && canViewPepm;
  const showPepm = isInternalUser || isClientAdmin;

  const rfcQuery = trpc.clients.rfc.getById.useQuery({rfcId}, {enabled: !!rfcId});
  const updateFormAdmin = trpc.clients.rfc.updateFormAdmin.useMutation({
    onError: (error) => surfaceRfcFormErrors(error),
  });

  const form = useForm<WizardFormData>({
    resolver: zodResolver(wizardFormSchema),
    defaultValues: getBlankRfcFormState(),
    mode: 'onChange',
  });

  // Seed the form once when the data first arrives. Re-running the reset on
  // every rfcQuery.data change would revert the user's in-progress edits on any
  // background refetch (window focus, stale timer): superjson revives Dates in
  // the payload, which defeats React Query's structural sharing, so each
  // refetch produces a fresh data reference even when nothing changed.
  const formSeeded = useRef(false);
  useEffect(() => {
    const data = rfcQuery.data;
    if (!data || formSeeded.current) return;
    formSeeded.current = true;
    form.reset({
      section125Form: data.section125FormData || getBlankRfcFormState().section125Form,
      mecForm: data.mecFormData || getBlankRfcFormState().mecForm,
      clientPortalAccessForm:
        data.clientPortalAccessData || getBlankRfcFormState().clientPortalAccessForm,
    });
  }, [rfcQuery.data, form]);

  const sections = useMemo(
    () =>
      getAdminSections(
        !!rfcQuery.data?.section125FormEnabled,
        !!rfcQuery.data?.mecFormEnabled,
        rfcQuery.data?.clientPortalAccessEnabled ?? true,
      ),
    [
      rfcQuery.data?.section125FormEnabled,
      rfcQuery.data?.mecFormEnabled,
      rfcQuery.data?.clientPortalAccessEnabled,
    ],
  );

  const onSave = async () => {
    if (!rfcId) return;
    const values = form.getValues();
    try {
      const response = await updateFormAdmin.mutateAsync({
        rfcId,
        formData: {
          section125FormData: values.section125Form,
          mecFormData: values.mecForm,
          clientPortalAccessFormData: values.clientPortalAccessForm,
        },
      });
      if (response) {
        toast({
          title: 'Success',
          description: 'The RFC was updated successfully.',
        });
        onSaved();
      }
    } catch (error) {
      // onError toasts the field list; map issues to fields for inline highlighting.
      applyRfcFieldErrors(error, form);
    }
  };

  if (rfcQuery.isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <div className='animate-pulse'>Loading editor...</div>
      </div>
    );
  }
  if (rfcQuery.error || !rfcQuery.data) {
    return (
      <Alert>
        <AlertDescription>{rfcQuery.error?.message || 'Failed to load RFC'}</AlertDescription>
      </Alert>
    );
  }

  const rfcData = rfcQuery.data;
  const isReadOnly =
    rfcData.status === RfcStatus.COMPLETED || rfcData.status === RfcStatus.ARCHIVED;

  return (
    <RfcContext.Provider
      value={{
        accessToken: '',
        password: '',
        section125FormEnabled: !!rfcData.section125FormEnabled,
        mecFormEnabled: !!rfcData.mecFormEnabled,
        attachments:
          rfcData.attachments?.map((a) => ({
            id: a.id,
            fileName: a.fileName,
            fileSize: a.fileSize,
            fileType: a.fileType as RfcAttachmentType,
          })) || [],
        refreshAttachments: () => rfcQuery.refetch(),
      }}
    >
      <FormProvider {...form}>
        <div className='space-y-8'>
          {sections.map((section) => (
            <div key={section.heading} className='space-y-4'>
              <h3 className='text-lg font-semibold text-brand-navy border-b border-gray-200 pb-2'>
                {section.heading}
              </h3>
              {section.items.map((item) => (
                <Card key={item.step} className='shadow-sm border-gray-200'>
                  <CardHeader className='bg-gray-50 border-b py-3'>
                    <CardTitle className='text-base font-medium text-brand-navy'>
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='p-6'>
                    <RfcStepForm
                      currentStep={item.step}
                      disableFormCopy={true}
                      showEnrollmentType
                      showPepm={showPepm}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
          <div className='flex justify-end gap-3 sticky bottom-0 bg-white py-4 border-t border-gray-200'>
            <Button type='button' variant='outline' onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type='button'
              onClick={onSave}
              disabled={updateFormAdmin.isPending || isReadOnly}
              className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold'
            >
              <Save className='h-4 w-4 mr-2' />
              {updateFormAdmin.isPending ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>
      </FormProvider>
    </RfcContext.Provider>
  );
}
